import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { listLibrary, saveArticle, articleToSocial, libraryMedia } from "@/lib/library.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { HourlyQueue } from "@/components/admin/QueuePanels";

type Media = Awaited<ReturnType<typeof libraryMedia>>[number];
const fmt = (iso: string) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Denver", month: "short", day: "numeric", hour: "numeric" });

function PostMedia({ items }: { items: Media[] }) {
  if (!items.length) return <p className="mt-2 text-xs text-muted-foreground">No social posts yet. Click → Social.</p>;
  return (
    <ul className="mt-2 space-y-1 border-t border-border pt-2">
      {items.map((m) => (
        <li key={m.id} className="text-xs">
          <Link to="/admin/social" search={{ draft: m.id }} className="font-medium text-foreground hover:underline">{m.headline}</Link>
          <div className="mt-0.5 flex flex-wrap gap-1">
            <Badge variant={m.graphics ? "default" : "outline"}>Graphics</Badge>
            <Badge variant={m.carousel ? "default" : "outline"}>Carousel</Badge>
            <Badge variant={m.video ? "default" : "outline"}>Video</Badge>
            <Badge variant="secondary">{m.slot ? `${m.slot.status === "published" ? "Posted" : "Scheduled"} ${fmt(m.slot.at)}` : m.graphics || m.carousel || m.video ? "Next open slot" : "Needs media"}</Badge>
          </div>
        </li>
      ))}
    </ul>
  );
}

export const Route = createFileRoute("/_authenticated/admin/library")({
  head: () => ({ meta: [{ title: "Admin · Content Library" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: Library,
});

type Article = Awaited<ReturnType<typeof listLibrary>>[number];
const blank = { id: undefined as string | undefined, title: "", excerpt: "", body_markdown: "", category: "Marketing", status: "draft" as "draft" | "published" };

function Library() {
  const list = useServerFn(listLibrary);
  const save = useServerFn(saveArticle);
  const toSocial = useServerFn(articleToSocial);
  const loadMedia = useServerFn(libraryMedia);
  const [media, setMedia] = useState<Media[]>([]);
  const [items, setItems] = useState<Article[]>([]);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState<string | null>(null);
  const [count, setCount] = useState(3);

  const refresh = useCallback(async () => { const [a, m] = await Promise.all([list(), loadMedia()]); setItems(a); setMedia(m); }, [list, loadMedia]);
  useEffect(() => { refresh().catch((e) => toast.error(e.message)); }, [refresh]);

  const edit = (a: Article) => setForm({ id: a.id, title: a.title, excerpt: a.excerpt, body_markdown: a.body_markdown, category: a.category, status: a.status === "published" ? "published" : "draft" });

  async function onSave(status: "draft" | "published") {
    setBusy("save");
    try {
      const r = await save({ data: { ...form, status } });
      setForm({ ...form, id: r.id, status });
      toast.success(status === "published" ? "Published" : "Saved");
      await refresh();
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(null); }
  }

  async function onSocial(id: string) {
    setBusy(id);
    try {
      const r = await toSocial({ data: { id, count } });
      toast.success(`${r.count} social drafts added to Social Studio`);
      await refresh();
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Content Library</h1>
          <p className="text-sm text-muted-foreground">Write blog posts, then turn them into Social Studio drafts.</p>
        </div>
        <Button asChild variant="outline"><Link to="/admin/social">Open Social Studio</Link></Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Card className="space-y-3 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground">{form.id ? "Edit post" : "New post"}</h2>
            {form.id && <Button size="sm" variant="ghost" onClick={() => setForm(blank)}>+ New post</Button>}
          </div>
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input placeholder="Category (e.g. SEO, Web Design)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Textarea rows={2} placeholder="Short summary (shows on blog cards)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          <Textarea rows={18} placeholder="Write your post here. Use ## for headings." value={form.body_markdown} onChange={(e) => setForm({ ...form, body_markdown: e.target.value })} className="font-mono text-sm" />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!!busy} onClick={() => onSave("draft")}>Save draft</Button>
            <Button disabled={!!busy} onClick={() => onSave("published")}>Publish to blog</Button>
            {form.id && <Button variant="secondary" disabled={!!busy} onClick={() => onSocial(form.id!)}>{busy === form.id ? "Writing…" : `Make ${count} social drafts`}</Button>}
          </div>
        </Card>

        <Card className="space-y-3 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground">Posts ({items.length})</h2>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              Drafts per post
              <Input type="number" min={1} max={5} value={count} onChange={(e) => setCount(Math.min(5, Math.max(1, Number(e.target.value) || 1)))} className="h-8 w-14" />
            </label>
          </div>
          <div className="max-h-[70vh] space-y-2 overflow-auto">
            {items.map((a) => (
              <div key={a.id} className="rounded-md border border-border p-3">
                <button className="text-left text-sm font-medium text-foreground hover:underline" onClick={() => edit(a)}>{a.title}</button>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <Badge variant={a.status === "published" ? "default" : "secondary"}>{a.status}</Badge>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => onSocial(a.id)}>
                    {busy === a.id ? "Writing…" : "→ Social"}
                  </Button>
                </div>
                <PostMedia items={media.filter((m) => a.slug && m.cta_url.endsWith(a.slug.startsWith("/") ? a.slug : `/learn/${a.slug}`))} />
              </div>
            ))}
            {!items.length && <p className="text-sm text-muted-foreground">No posts yet.</p>}
          </div>
        </Card>
      </div>
      <HourlyQueue onChange={() => refresh().catch((e) => toast.error(e.message))} />
    </div>
  );
}
